import { get, set } from 'idb-keyval';
import posthog from 'posthog-js';
import type { CaptureOptions } from 'posthog-js';
import { isEffectivelyOnline } from '@bilderit/offline-link';
import { isPostHogInitialised } from './posthog';
import { currentScreenProperties } from './taxonomy';

/**
 * Persistent, offline-aware analytics event queue.
 *
 * PostHog's browser SDK only buffers in memory — a maquinista operator who
 * works a morning offline and then closes or reloads the tab loses every
 * event from that session. This module persists events to IndexedDB (via
 * idb-keyval, the same library `@bilderit/offline-link`'s `trackedQueries`
 * store uses) and flushes them once the app is back online.
 *
 * This queue is deliberately NOT `trackedQueries` and does not share its
 * failure policy:
 * - `trackedQueries` holds user data (partes), replays in order, and stops
 *   at the first failure to preserve causal ordering
 *   (OfflineMutationLoader.ts).
 * - This queue holds analytics events, which are causally independent and
 *   droppable. A flush never stops at the first failure — one bad event
 *   must not hold the rest of the queue hostage — and, unlike
 *   `trackedQueries`'s upgrade-safe schema guard, a `SCHEMA_VERSION`
 *   mismatch here simply clears the store.
 *
 * Callers reach this module through `capture()` (capture.ts), never
 * directly.
 */

const KEY = 'analyticsEvents';
const SCHEMA_VERSION = '1';
const SCHEMA_VERSION_KEY = 'analyticsEvents-schema-version';

// A device offline for a week must not grow an unbounded store — analytics
// must never be the reason a PWA runs out of storage. On overflow the
// oldest events are dropped first (see enqueueAnalyticsEvent).
const MAX_QUEUE_SIZE = 500;

// Matches createOfflineLink's ONLINE_THRESHOLD (OfflineLink.ts) and
// connectivity.ts's CONNECTIVITY_THRESHOLD — "online" means the same thing
// everywhere in this app. Flushing while only marginally connected would
// hand events to PostHog's own in-memory transport, which is exactly the
// volatile buffer this queue exists to avoid feeding while unreliable.
const FLUSH_THRESHOLD = '3g';
const FLUSH_INTERVAL_MS = 30 * 1000;

export type CaptureProperties = Record<
  string,
  string | number | boolean | undefined
>;

type DroppedCounts = {
  overflow: number;
  identity_mismatch: number;
};

export type QueuedAnalyticsEvent = {
  id: string;
  event: string;
  properties?: CaptureProperties;
  /**
   * The PostHog `distinct_id` current at enqueue time (read via
   * `posthog.get_distinct_id()`). Operators share tablets, so an event
   * queued while identified as one person and flushed after another signs
   * in must never be sent under the wrong identity — see
   * flushAnalyticsQueue(). This is NOT enforced for events enqueued before
   * `posthog.identify()` ever ran (see `identified` below): PostHog merges
   * the anonymous person into the identified user on `identify`, so sending
   * anonymous-origin events under the now-identified id is exactly what
   * PostHog's own semantics do — it is not a misattribution.
   */
  distinctId: string;
  /**
   * Whether PostHog had already been `identify()`-d at enqueue time (read
   * via `posthog._isIdentified()`). Gates the mismatch check above: an
   * anonymous-origin event (identified: false) is always sent regardless of
   * who is signed in at flush time — there is no "wrong identity" to
   * protect against yet, only the pre-identify anonymous id that PostHog
   * itself reconciles on identify(). Only an event enqueued while ALREADY
   * identified as someone else is held to the strict "never misattribute"
   * rule.
   */
  identified: boolean;
  /** Original client timestamp (epoch ms) — sent to PostHog at flush time
   * so a morning's queued work is attributed to when it happened, not to
   * when the batch was replayed. */
  enqueuedAt: number;
  /**
   * The PostHog groups (`{ company: '<id>' }`) active when the event was
   * captured. posthog-js stamps `$groups` from persistence at capture time,
   * i.e. at FLUSH time for a queued event — so without this snapshot an
   * event queued under company A and flushed after the manager switched to
   * company B would be attributed to B. Replayed explicitly on flush
   * (an explicit `$groups` property wins over the persisted one in the
   * installed SDK). Absent (undefined) when there was no group yet.
   */
  groups?: Record<string, string>;
};

type Store = {
  events: QueuedAnalyticsEvent[];
  dropped: DroppedCounts;
};

const emptyStore = (): Store => ({
  events: [],
  dropped: { overflow: 0, identity_mismatch: 0 },
});

const getStore = async (): Promise<Store> => {
  const raw = await get(KEY);
  if (!raw) return emptyStore();
  try {
    const parsed = JSON.parse(raw);
    return {
      events: Array.isArray(parsed.events) ? parsed.events : [],
      dropped: {
        overflow: parsed.dropped?.overflow ?? 0,
        identity_mismatch: parsed.dropped?.identity_mismatch ?? 0,
      },
    };
  } catch {
    return emptyStore();
  }
};

// Serialises all read-modify-write operations on the store so concurrent
// calls (an enqueue racing a flush, or two flushes) cannot clobber each
// other's writes. The IDB key is shared by every tab on the origin, so the
// lock is cross-tab where the browser supports it (Web Locks API — Chrome
// 69+, Safari 15.4+; i.e. the whole supported floor): a flush in one tab
// and an enqueue in another take turns instead of overwriting each other's
// store. Where `navigator.locks` is missing (jsdom, very old WebViews) it
// falls back to the in-tab promise chain, which is what this was before.
// Mirrors trackedQueries.ts's withStoreLock — this is a SEPARATE lock
// guarding a separate IDB key.
const LOCK_NAME = 'bilderit-analytics-queue';
let queueTail: Promise<unknown> = Promise.resolve();
const withStoreLock = <T>(fn: () => Promise<T>): Promise<T> => {
  const locks =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & { locks?: LockManager }).locks
      : undefined;
  if (locks && typeof locks.request === 'function') {
    return locks.request(LOCK_NAME, fn) as Promise<T>;
  }
  const run = queueTail.then(fn, fn);
  queueTail = run.catch(() => undefined);
  return run;
};

const updateSchema = (): void => {
  try {
    const currentVersion = window.localStorage.getItem(SCHEMA_VERSION_KEY);
    if (currentVersion === SCHEMA_VERSION) return;
    window.localStorage.setItem(SCHEMA_VERSION_KEY, SCHEMA_VERSION);
    // Unlike trackedQueries' upgrade-safe migration, analytics events are
    // droppable — clearing the store on any schema change is the correct,
    // simple guard here (see plan 010, "Current state"). Routed through the
    // store lock so this write can never interleave with the very first
    // enqueue's read-modify-write (both can run at module-load time).
    void withStoreLock(async () => {
      await set(KEY, JSON.stringify(emptyStore()));
    });
  } catch {
    // IndexedDB/localStorage unavailable — degrade to no-op, never throw.
  }
};
updateSchema();

let idCounter = 0;
const nextId = (): string => `${Date.now()}-${++idCounter}`;

/**
 * Test/inspection helpers. Routed through the same store lock as
 * enqueue/flush so a caller that awaits one of these is guaranteed to see
 * every fire-and-forget enqueue issued before it — enqueueAnalyticsEvent
 * itself must stay unawaited (see its docstring), so tests await these
 * instead to drain the write queue before asserting.
 */
export const getQueuedEvents = (): Promise<QueuedAnalyticsEvent[]> =>
  withStoreLock(async () => (await getStore()).events);

export const getDroppedCounts = (): Promise<DroppedCounts> =>
  withStoreLock(async () => (await getStore()).dropped);

export const clearAnalyticsQueue = (): Promise<void> =>
  withStoreLock(async () => {
    await set(KEY, JSON.stringify(emptyStore()));
  });

/**
 * Drops the anonymous-origin entries (identified: false) — called when the
 * identity is reset on sign-out. The carve-out that always sends those
 * entries is only right within one person's session: an anonymous pageview
 * queued before user A identified, left behind by A's sign-out, would
 * otherwise be sent under whoever signs in next on the same device.
 * Identified entries are kept: they carry their own distinctId and the
 * flush drops them itself if someone else is signed in by then.
 */
export const clearAnonymousQueuedEvents = (): Promise<void> =>
  withStoreLock(async () => {
    let store: Store;
    try {
      store = await getStore();
    } catch (error) {
      console.error('Failed to read analytics queue', error);
      return;
    }
    const identifiedOnly = store.events.filter((entry) => entry.identified);
    if (identifiedOnly.length === store.events.length) return;
    try {
      await set(
        KEY,
        JSON.stringify({ ...store, events: identifiedOnly } satisfies Store)
      );
    } catch (error) {
      console.error('Failed to drop anonymous analytics events', error);
    }
  });

/**
 * posthog-js sends a capture straight away, outside its request queue,
 * whenever it is given an options object without an internal batch key
 * (`_send_request`: `options && !options._batchKey` → send now). Every
 * queued entry needs the options object for its original `timestamp`, so a
 * full drain would otherwise turn into one HTTP request per event — up to
 * 500 in a row on a tablet coming back online. The key is what PostHog's own
 * exception capture uses; not in the public CaptureOptions type, hence the
 * cast. Same batching, per-event timestamps preserved.
 */
const ANALYTICS_QUEUE_BATCH_KEY = 'analyticsQueue';
const captureOptionsFor = (entry: QueuedAnalyticsEvent): CaptureOptions =>
  ({
    timestamp: new Date(entry.enqueuedAt),
    _batchKey: ANALYTICS_QUEUE_BATCH_KEY,
  }) as CaptureOptions;

/**
 * Enqueues one analytics event for later delivery. Fire-and-forget: never
 * awaited by callers, never throws, never blocks a UI interaction. A no-op
 * when PostHog was never initialised (no VITE_APP_POSTHOG_KEY) so the store
 * never accumulates events that would never drain, and a no-op (logged, not
 * thrown) when IndexedDB is unavailable or throws — private browsing and
 * storage-pressure eviction both cause this on mobile Safari.
 */
export const enqueueAnalyticsEvent = (
  event: string,
  properties?: CaptureProperties
): void => {
  if (!isPostHogInitialised()) return;

  let distinctId: string;
  try {
    distinctId = posthog.get_distinct_id();
  } catch (error) {
    console.error(
      'Failed to read distinct id; dropping analytics event',
      event,
      error
    );
    return;
  }

  // `_isIdentified()` is a typed (if underscore-prefixed) method on the
  // installed posthog-js version — verified in node_modules/posthog-js/dist/module.d.ts.
  // If it ever throws (or is missing on some future version), default to
  // `true`: that applies the stricter "never misattribute" mismatch check
  // rather than silently loosening it — see the `identified` field's
  // docstring above.
  let identified = true;
  try {
    identified = posthog._isIdentified();
  } catch (error) {
    console.error(
      'Failed to read PostHog identified state; treating as identified',
      event,
      error
    );
  }

  let groups: Record<string, string> | undefined;
  try {
    const activeGroups = posthog.getGroups() as Record<string, string>;
    groups = Object.keys(activeGroups).length > 0 ? activeGroups : undefined;
  } catch {
    groups = undefined;
  }

  const entry: QueuedAnalyticsEvent = {
    id: nextId(),
    event,
    properties,
    distinctId,
    identified,
    enqueuedAt: Date.now(),
    ...(groups ? { groups } : {}),
  };

  void withStoreLock(async () => {
    try {
      const store = await getStore();
      store.events.push(entry);
      if (store.events.length > MAX_QUEUE_SIZE) {
        const overflow = store.events.length - MAX_QUEUE_SIZE;
        store.events.splice(0, overflow);
        store.dropped.overflow += overflow;
      }
      await set(KEY, JSON.stringify(store));
    } catch (error) {
      console.error('Failed to enqueue analytics event', event, error);
    }
  });
};

/**
 * Sends every queued event whose recorded identity still matches the
 * current one, removing only what was sent successfully. Never stops at the
 * first failure — a failed event is kept for the next attempt while every
 * other event in the batch is still sent, the key behavioural difference
 * from OfflineMutationLoader's ordered replay (analytics events are
 * causally independent).
 *
 * A no-op when PostHog isn't initialised, when the connection is below the
 * online threshold (handing events to the SDK's in-memory transport while
 * still offline would defeat the point of this queue), or when IndexedDB
 * throws.
 */
export const flushAnalyticsQueue = (): Promise<void> =>
  withStoreLock(async () => {
    if (!isPostHogInitialised()) return;
    if (!isEffectivelyOnline(FLUSH_THRESHOLD)) return;

    let store: Store;
    try {
      store = await getStore();
    } catch (error) {
      console.error('Failed to read analytics queue', error);
      return;
    }

    if (
      store.events.length === 0 &&
      store.dropped.overflow === 0 &&
      store.dropped.identity_mismatch === 0
    ) {
      return;
    }

    let currentDistinctId: string;
    try {
      currentDistinctId = posthog.get_distinct_id();
    } catch (error) {
      console.error(
        'Failed to read current distinct id; skipping this flush',
        error
      );
      return;
    }

    const remaining: QueuedAnalyticsEvent[] = [];
    let identityMismatchDropped = 0;
    let sentCount = 0;

    for (const entry of store.events) {
      // Only an entry recorded while ALREADY identified as someone else is
      // held to the "never misattribute" rule. An anonymous-origin entry
      // (identified: false) is always sent: PostHog merges the anonymous
      // person into the identified user on identify(), so sending it under
      // the now-identified id is exactly what PostHog's own semantics do —
      // see the `identified` field's docstring. Without this carve-out,
      // every session's pre-identify events (e.g. the first pageview, fired
      // before RegisterPostHogUser's identify() effect runs) would be
      // dropped on every single flush.
      if (entry.identified && entry.distinctId !== currentDistinctId) {
        // Never send under the wrong identity — operators share tablets.
        // Counted, never sent (see "The identity the events carry", plan 010).
        identityMismatchDropped += 1;
        continue;
      }
      try {
        posthog.capture(
          entry.event,
          entry.groups
            ? { ...(entry.properties ?? {}), $groups: entry.groups }
            : entry.properties,
          captureOptionsFor(entry)
        );
        sentCount += 1;
      } catch (error) {
        console.error('Failed to flush analytics event', entry.event, error);
        remaining.push(entry);
      }
    }

    let dropped: DroppedCounts = {
      overflow: store.dropped.overflow,
      identity_mismatch:
        store.dropped.identity_mismatch + identityMismatchDropped,
    };

    // Report the dropped-event health signal alongside the next successful
    // flush, only when non-zero, then reset it.
    if (
      sentCount > 0 &&
      (dropped.overflow > 0 || dropped.identity_mismatch > 0)
    ) {
      try {
        posthog.capture('analytics_queue_flushed', {
          // Every event carries `module` and `route_name` (contract); the
          // others get them from capture(), this one is sent from the queue
          // itself, so it stamps them inline.
          ...currentScreenProperties(),
          dropped_overflow: dropped.overflow,
          dropped_identity_mismatch: dropped.identity_mismatch,
        });
        dropped = { overflow: 0, identity_mismatch: 0 };
      } catch (error) {
        console.error('Failed to report dropped analytics events', error);
        // Keep the counts — retried on the next successful flush.
      }
    }

    try {
      await set(KEY, JSON.stringify({ events: remaining, dropped }));
    } catch (error) {
      console.error('Failed to persist analytics queue after flush', error);
    }
  });

/**
 * Wires the queue's own triggers: an immediate boot drain (whatever
 * survived the last session), a 30-second periodic flush while online, and
 * a page-hide flush. Call once, only from initPostHog()'s success path —
 * see posthog.ts. The online-edge flush is wired separately, by reusing
 * connectivity.ts's existing network-status registration rather than
 * registering a second one.
 */
export const startAnalyticsQueueFlushing = (): void => {
  void flushAnalyticsQueue();

  setInterval(() => {
    void flushAnalyticsQueue();
  }, FLUSH_INTERVAL_MS);

  document.addEventListener('visibilitychange', () => {
    // 'hidden', not 'unload' — unload is unreliable on mobile Safari.
    if (document.visibilityState === 'hidden') void flushAnalyticsQueue();
  });
};
